<script setup>
import Dialog from 'primevue/dialog';
import { ref, computed } from 'vue';
import { useUserStore } from '@/store/user-store';
import AnButton from '../common/AnButton.vue';

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(['update:modelValue']);
const store = useUserStore();

const liabilityNames = [
  'Credit Card', 'Home Loan', 'Car Loan', 'Personal Loan'
];
const selectedName = ref(liabilityNames[0]);
const interestRate = ref(0);
const emiAmount = ref('');
const amountOutstanding = ref('');
const description = ref('');
const date = ref('');
const error = ref('');

const closeDialog = () => emit('update:modelValue', false);

const handleAdd = async () => {
  // Find itemId if exists
  const existing = (store.financialStatement?.customerLiabilities || []).find(
    l => l.liabilityNm === selectedName.value
  );
  const payload = {
    records: [{
      itemId: existing ? existing.itemId : null,
      liabilityName: selectedName.value,
      loanInterestRate: interestRate.value,
      loanEMIAmount: emiAmount.value,
      liabilityDescription: description.value,
      liabilityDate: date.value,
      amountOutstanding: amountOutstanding.value
    }]
  };
  try {
    await store.updateFinancialStatement(payload);
    closeDialog();
  } catch (e) {
    error.value = 'Failed to update liability.';
  }
};
</script>
<template>
  <Dialog modal :visible="modelValue" :closable="false" :dismissable-mask="false" @update:visible="closeDialog">
    <i @click="closeDialog" class="pi pi-times close-btn" />
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">Manage Liability</span>
      </div>
    </template>
    <div class="dialog-content">
      <div class="dialog-section">
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">Liability Name</label>
            <select v-model="selectedName" class="dialog-input">
              <option v-for="name in liabilityNames" :key="name" :value="name">{{ name }}</option>
            </select>
          </div>
          <div class="dialog-col">
            <label class="dialog-label">Liability Description</label>
            <input v-model="description" class="dialog-input" placeholder="Description" />
          </div>
        </div>
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">Interest Rate <span class="rate-value">{{ interestRate }}%</span></label>
            <div class="slider-wrap">
              <span>0%</span>
              <input type="range" min="0" max="40" v-model="interestRate" class="dialog-slider" />
              <span>40%</span>
            </div>
          </div>
          <div class="dialog-col">
            <label class="dialog-label">EMI Amount</label>
            <input v-model="emiAmount" class="dialog-input" type="number" placeholder="EMI Amount" />
          </div>
        </div>
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">Amount Outstanding</label>
            <input v-model="amountOutstanding" class="dialog-input" type="number" placeholder="Amount Outstanding" />
          </div>
          <div class="dialog-col">
            <label class="dialog-label">Date</label>
            <input v-model="date" class="dialog-input" type="date" />
          </div>
        </div>
      </div>
      <div v-if="error" class="error-msg">{{ error }}</div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <AnButton label="Back" variant="tab" @click="closeDialog" />
        <AnButton label="Add" variant="primary" @click="handleAdd" />
      </div>
    </template>
  </Dialog>
</template>
<style scoped>
:deep(.p-dialog), :deep(.p-dialog-content), :deep(.p-dialog-header), :deep(.p-dialog-footer) {
  width: 420px !important;
  max-width: 440px !important;
  min-width: 320px !important;
  box-sizing: border-box;
}
.dialog-content {
  padding: 18px 18px 0 18px;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}
.dialog-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 12px;
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 0 0 0;
  min-height: 40px;
  border-bottom: 1px solid #e2e8f0;
}
.dialog-title {
  color: var(--an-primary);
  font-size: 20px;
  font-weight: 700;
  flex: 1;
  text-align: center;
  margin: 0 auto;
  padding-left: 12px;
}
.close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  background: var(--an-error);
  color: #fff;
  padding: 6px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 20px;
  z-index: 10;
}
.dialog-row-flex {
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-bottom: 36px;
}
.dialog-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}
.dialog-label {
  font-weight: 600;
  margin-bottom: 2px;
  font-size: 15px;
}
.rate-value {
  color: var(--an-primary);
  font-weight: 700;
  margin-left: 8px;
  font-size: 15px;
}
.dialog-input {
  padding: 12px;
  border: 1.5px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
}
.dialog-input:focus {
  outline: none;
  border-color: var(--an-primary);
}
.slider-wrap {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
}
.dialog-slider {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin-bottom: 2px;
  margin-right: 8px;
}
.slider-labels {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: auto;
  font-size: 13px;
  color: #444;
  gap: 8px;
}
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 14px;
  width: 100%;
  padding: 10px 0 0 0;
  margin-bottom: 18px;
}
.error-msg {
  color: #c92a2a;
  font-size: 1rem;
  margin: 0 12px 8px 12px;
}
@media (max-width: 600px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }
  .dialog-row-flex {
    flex-direction: column !important;
    gap: 6px !important;
  }
}
</style>
